import React, { useEffect, useState } from "react";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { storage } from "../../../firebase";
import { Button } from "../../../design/button";
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from "../../../design/dialog";
import { Input } from "../../../design/input";
import { Select } from "../../../design/select";
import {
  Field,
  Fieldset,
  FieldGroup,
  Label,
  ErrorMessage,
  Description,
} from "../../../design/fieldset";
import { useChild } from "../../../contexts/ChildContext";
import { useAccount } from "../../../contexts/AccountContext";
import { Child, storagePaths } from "@visitproof/types";
import { Textarea } from "../../../design/textarea";
import { Switch, SwitchField } from "../../../design/switch";
import { parseDateToTimestamp } from "../../../utils/date";
import {
  ChevronDownIcon,
  ChevronUpIcon,
} from "@heroicons/react/20/solid";
import {
  Race,
  Sex,
  PrimaryLanguage,
  SpecialNeed,
  VictimizationType,
  RACE_LABELS,
  SEX_LABELS,
  LANGUAGE_LABELS,
  SPECIAL_NEEDS_LABELS,
  VICTIMIZATION_TYPE_LABELS,
} from "@visitproof/types";
import ProfileImageUpload from "../../../components/ProfileImageUpload";
import { getInitials } from "../../../utils/string";

interface Props {
  child: Child;
}

interface Errors {
  firstName?: string;
  lastName?: string;
  email?: string;
  dob?: string;
}

// Collapsible section component for better organization
const CollapsibleSection: React.FC<{
  title: string;
  description?: string;
  isExpanded: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}> = ({ title, description, isExpanded, onToggle, children }) => (
  <div className="overflow-hidden border rounded-lg border-zinc-200 dark:border-zinc-700">
    <button
      type="button"
      onClick={onToggle}
      className="flex items-center justify-between w-full px-4 py-3 text-left bg-zinc-50 dark:bg-zinc-800/50 hover:bg-zinc-100 dark:hover:bg-zinc-800"
    >
      <div>
        <span className="font-medium text-zinc-900 dark:text-zinc-100">{title}</span>
        {description && (
          <p className="text-sm text-zinc-500 dark:text-zinc-400">{description}</p>
        )}
      </div>
      {isExpanded ? (
        <ChevronUpIcon className="w-5 h-5 text-zinc-500" />
      ) : (
        <ChevronDownIcon className="w-5 h-5 text-zinc-500" />
      )}
    </button>
    {isExpanded && <div className="px-4 py-4">{children}</div>}
  </div>
);

const EditChildButton: React.FC<Props> = ({ child }) => {
  const { updateChild } = useChild();
  const { account } = useAccount();
  const [isOpen, setIsOpen] = useState(false);
  const [file, setFile] = useState<File | null>(null);

  // Section expansion state
  const [expandedSections, setExpandedSections] = useState({
    demographics: false,
    specialNeeds: false,
    medical: false,
    custody: false,
    dss: false,
    victimization: false,
  });

  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  // Basic info
  const [firstName, setFirstName] = useState<string>(child.firstName ?? "");
  const [lastName, setLastName] = useState<string>(child.lastName ?? "");
  const [dob, setDob] = useState<string>(
    child.dob ? child.dob.toDate().toISOString().split("T")[0] : ""
  );
  const [allergies, setAllergies] = useState<string>(child.allergies ?? "");
  const [hasSpecialNeeds, setHasSpecialNeeds] = useState<boolean>(
    child.hasSpecialNeeds ?? false
  );
  const [notes, setNotes] = useState<string>(child.notes ?? "");
  const [errors, setErrors] = useState<Errors>({});
  const [isSaving, setIsSaving] = useState(false);

  // Demographics state
  const [race, setRace] = useState<Race | "">(child.race ?? "");
  const [sex, setSex] = useState<Sex | "">(child.sex ?? "");
  const [primaryLanguage, setPrimaryLanguage] = useState<PrimaryLanguage | "">(
    child.primaryLanguage ?? ""
  );
  const [primaryLanguageOther, setPrimaryLanguageOther] = useState<string>(
    child.primaryLanguageOther ?? ""
  );
  const [specialNeeds, setSpecialNeeds] = useState<SpecialNeed[]>(
    child.specialNeeds ?? []
  );
  const [specialNeedsNotes, setSpecialNeedsNotes] = useState<string>(
    child.specialNeedsNotes ?? ""
  );

  // Custody/Guardian state
  const [residesWithName, setResidesWithName] = useState<string>(
    child.residesWithName ?? ""
  );
  const [legalGuardianName, setLegalGuardianName] = useState<string>(
    child.legalGuardianName ?? ""
  );

  // DSS state
  const [isInDSSCustody, setIsInDSSCustody] = useState<boolean>(
    child.isInDSSCustody ?? false
  );
  const [socialWorkerName, setSocialWorkerName] = useState<string>(
    child.socialWorkerName ?? ""
  );
  const [socialWorkerPhone, setSocialWorkerPhone] = useState<string>(
    child.socialWorkerPhone ?? ""
  );

  // Medical state
  const [medicalProblems, setMedicalProblems] = useState<string>(
    child.medicalProblems ?? ""
  );

  // Victimization state
  const [victimizationType, setVictimizationType] = useState<VictimizationType | "">(
    child.victimizationType ?? ""
  );
  const [victimizationOther, setVictimizationOther] = useState<string>(
    child.victimizationOther ?? ""
  );
  const [hasWitnessedAbuse, setHasWitnessedAbuse] = useState<boolean>(
    child.hasWitnessedAbuse ?? false
  );

  useEffect(() => {
    if (isOpen) {
      setFirstName(child.firstName ?? "");
      setLastName(child.lastName ?? "");
      setDob(child.dob ? child.dob.toDate().toISOString().split("T")[0] : "");
      setHasSpecialNeeds(child.hasSpecialNeeds ?? false);
      setAllergies(child.allergies ?? "");
      setNotes(child.notes ?? "");
      // Demographics
      setRace(child.race ?? "");
      setSex(child.sex ?? "");
      setPrimaryLanguage(child.primaryLanguage ?? "");
      setPrimaryLanguageOther(child.primaryLanguageOther ?? "");
      setSpecialNeeds(child.specialNeeds ?? []);
      setSpecialNeedsNotes(child.specialNeedsNotes ?? "");
      // Custody/Guardian
      setResidesWithName(child.residesWithName ?? "");
      setLegalGuardianName(child.legalGuardianName ?? "");
      // DSS
      setIsInDSSCustody(child.isInDSSCustody ?? false);
      setSocialWorkerName(child.socialWorkerName ?? "");
      setSocialWorkerPhone(child.socialWorkerPhone ?? "");
      // Medical
      setMedicalProblems(child.medicalProblems ?? "");
      // Victimization
      setVictimizationType(child.victimizationType ?? "");
      setVictimizationOther(child.victimizationOther ?? "");
      setHasWitnessedAbuse(child.hasWitnessedAbuse ?? false);
      // Reset section expansion
      setExpandedSections({
        demographics: false,
        specialNeeds: false,
        medical: false,
        custody: false,
        dss: false,
        victimization: false,
      });
    }
  }, [child, isOpen]);

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};
    if (!firstName.trim()) {
      newErrors.firstName = "First name is required.";
    }
    if (!lastName.trim()) {
      newErrors.lastName = "Last name is required.";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }
    setIsSaving(true);

    try {
      let data: Partial<Child> = {
        firstName,
        lastName,
        dob: dob ? parseDateToTimestamp(dob) : undefined,
        hasSpecialNeeds,
        allergies: allergies || undefined,
        notes: notes || undefined,
        // Demographics
        race: race || undefined,
        sex: sex || undefined,
        primaryLanguage: primaryLanguage || undefined,
        primaryLanguageOther: primaryLanguage === "other" ? primaryLanguageOther : undefined,
        specialNeeds: specialNeeds.length > 0 ? specialNeeds : undefined,
        specialNeedsNotes: specialNeedsNotes || undefined,
        // Custody/Guardian
        residesWithName: residesWithName || undefined,
        legalGuardianName: legalGuardianName || undefined,
        // DSS
        isInDSSCustody,
        socialWorkerName: isInDSSCustody ? socialWorkerName || undefined : undefined,
        socialWorkerPhone: isInDSSCustody ? socialWorkerPhone || undefined : undefined,
        // Medical
        medicalProblems: medicalProblems || undefined,
        // Victimization
        victimizationType: victimizationType || undefined,
        victimizationOther: victimizationType === "other" ? victimizationOther : undefined,
        hasWitnessedAbuse,
      };
      if (!child.id) {
        throw new Error("Child ID is required");
      }
      if (file) {
        const path = account?.id
          ? storagePaths.childAvatar(account.id, child.id, file.name)
          : `children/${child.id}/${file.name}`;
        const fileRef = ref(storage, path);
        await uploadBytes(fileRef, file);
        const avatarUrl = await getDownloadURL(fileRef);
        data = { ...data, avatarUrl };
      }
      await updateChild(child.id, data);
      clearForm();
    } catch (error) {
      console.error("Error uploading file:", error);
      alert("Failed to update child. Please try again later.");
    }

    setIsSaving(false);
  };

  const handleSpecialNeedToggle = (need: SpecialNeed) => {
    setSpecialNeeds((prev) =>
      prev.includes(need)
        ? prev.filter((n) => n !== need)
        : [...prev, need]
    );
  };

  const clearForm = () => {
    setIsOpen(false);
    setFile(null);
    setErrors({});
  };

  return (
    <>
      <Button type="button" onClick={() => setIsOpen(true)}>
        Edit
      </Button>
      <Dialog open={isOpen} onClose={clearForm} size="2xl">
        <DialogTitle>Edit Child</DialogTitle>
        <DialogBody className="max-h-[70vh] overflow-y-auto">
          {/* Basic Info - Always visible */}
          <Fieldset>
            {/* Profile Image */}
            <div className="flex justify-center mb-6">
              <ProfileImageUpload
                currentImageUrl={child.avatarUrl}
                initials={getInitials(`${firstName} ${lastName}`)}
                onFileSelected={setFile}
                size="lg"
                disabled={isSaving}
              />
            </div>

            <FieldGroup>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Field>
                  <Label>First Name <span className="text-red-500">*</span></Label>
                  <Input
                    type="text"
                    value={firstName}
                    placeholder="First Name"
                    onChange={(e) => setFirstName(e.target.value)}
                  />
                  {errors.firstName && (
                    <ErrorMessage>{errors.firstName}</ErrorMessage>
                  )}
                </Field>
                <Field>
                  <Label>Last Name <span className="text-red-500">*</span></Label>
                  <Input
                    type="text"
                    value={lastName}
                    placeholder="Last Name"
                    onChange={(e) => setLastName(e.target.value)}
                  />
                  {errors.lastName && (
                    <ErrorMessage>{errors.lastName}</ErrorMessage>
                  )}
                </Field>
              </div>
            </FieldGroup>

            <FieldGroup>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Field>
                  <Label>Date of Birth</Label>
                  <Input
                    type="date"
                    value={dob}
                    placeholder="Date of Birth"
                    onChange={(e) => setDob(e.target.value)}
                  />
                  {errors.dob && <ErrorMessage>{errors.dob}</ErrorMessage>}
                </Field>
                <Field>
                  <Label>Allergies</Label>
                  <Input
                    type="text"
                    value={allergies}
                    placeholder="Known allergies"
                    onChange={(e) => setAllergies(e.target.value)}
                  />
                </Field>
              </div>
            </FieldGroup>

            <FieldGroup>
              <Field>
                <Label>Notes</Label>
                <Textarea
                  rows={3}
                  value={notes}
                  placeholder="Notes"
                  onChange={(e) => setNotes(e.target.value)}
                />
              </Field>
            </FieldGroup>
          </Fieldset>

          {/* Collapsible Sections */}
          <div className="mt-6 space-y-3">
            {/* Demographics */}
            <CollapsibleSection
              title="Demographics"
              description="For grant reporting purposes"
              isExpanded={expandedSections.demographics}
              onToggle={() => toggleSection("demographics")}
            >
              <div className="space-y-4">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <Field>
                    <Label>Sex</Label>
                    <Select
                      value={sex}
                      onChange={(e) => setSex(e.target.value as Sex)}
                    >
                      <option value="">Select...</option>
                      {Object.entries(SEX_LABELS).map(([value, label]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ))}
                    </Select>
                  </Field>
                  <Field>
                    <Label>Race/Ethnicity</Label>
                    <Select
                      value={race}
                      onChange={(e) => setRace(e.target.value as Race)}
                    >
                      <option value="">Select...</option>
                      {Object.entries(RACE_LABELS).map(([value, label]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ))}
                    </Select>
                  </Field>
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <Field>
                    <Label>Primary Language</Label>
                    <Select
                      value={primaryLanguage}
                      onChange={(e) => setPrimaryLanguage(e.target.value as PrimaryLanguage)}
                    >
                      <option value="">Select...</option>
                      {Object.entries(LANGUAGE_LABELS).map(([value, label]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ))}
                    </Select>
                  </Field>
                  {primaryLanguage === "other" && (
                    <Field>
                      <Label>Other Language</Label>
                      <Input
                        type="text"
                        value={primaryLanguageOther}
                        placeholder="Specify language"
                        onChange={(e) => setPrimaryLanguageOther(e.target.value)}
                      />
                    </Field>
                  )}
                </div>
              </div>
            </CollapsibleSection>

            {/* Special Needs */}
            <CollapsibleSection
              title="Special Needs"
              description="Special needs and accommodations"
              isExpanded={expandedSections.specialNeeds}
              onToggle={() => toggleSection("specialNeeds")}
            >
              <div className="space-y-4">
                <SwitchField>
                  <Label>Has Special Needs</Label>
                  <Switch
                    checked={hasSpecialNeeds}
                    onChange={() => setHasSpecialNeeds(!hasSpecialNeeds)}
                  />
                </SwitchField>

                {hasSpecialNeeds && (
                  <>
                    <Field>
                      <Label>Special Needs Categories</Label>
                      <Description className="mb-2 text-sm text-zinc-500 dark:text-zinc-400">
                        Select all that apply
                      </Description>
                      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                        {Object.entries(SPECIAL_NEEDS_LABELS).map(([value, label]) => (
                          <label
                            key={value}
                            className="flex items-center gap-2 cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={specialNeeds.includes(value as SpecialNeed)}
                              onChange={() => handleSpecialNeedToggle(value as SpecialNeed)}
                              className="w-4 h-4 text-blue-600 rounded border-zinc-300 focus:ring-blue-500 dark:border-zinc-600 dark:bg-zinc-800"
                            />
                            <span className="text-sm text-zinc-700 dark:text-zinc-300">
                              {label}
                            </span>
                          </label>
                        ))}
                      </div>
                    </Field>

                    <Field>
                      <Label>Special Needs Notes</Label>
                      <Textarea
                        rows={2}
                        value={specialNeedsNotes}
                        placeholder="Additional details about special needs..."
                        onChange={(e) => setSpecialNeedsNotes(e.target.value)}
                      />
                    </Field>
                  </>
                )}
              </div>
            </CollapsibleSection>

            {/* Medical Information */}
            <CollapsibleSection
              title="Medical Information"
              description="Health conditions staff should be aware of"
              isExpanded={expandedSections.medical}
              onToggle={() => toggleSection("medical")}
            >
              <Field>
                <Label>Medical Conditions</Label>
                <Input
                  type="text"
                  value={medicalProblems}
                  placeholder="Any medical conditions staff should be aware of"
                  onChange={(e) => setMedicalProblems(e.target.value)}
                />
              </Field>
            </CollapsibleSection>

            {/* Custody & Guardian */}
            <CollapsibleSection
              title="Custody & Guardian"
              description="Living arrangements and legal guardianship"
              isExpanded={expandedSections.custody}
              onToggle={() => toggleSection("custody")}
            >
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Field>
                  <Label>Child Resides With</Label>
                  <Input
                    type="text"
                    value={residesWithName}
                    placeholder="Name of person child resides with"
                    onChange={(e) => setResidesWithName(e.target.value)}
                  />
                </Field>
                <Field>
                  <Label>Legal Guardian</Label>
                  <Input
                    type="text"
                    value={legalGuardianName}
                    placeholder="Legal guardian's name"
                    onChange={(e) => setLegalGuardianName(e.target.value)}
                  />
                </Field>
              </div>
            </CollapsibleSection>

            {/* DSS / Social Services */}
            <CollapsibleSection
              title="DSS / Social Services"
              description="Department of Social Services involvement"
              isExpanded={expandedSections.dss}
              onToggle={() => toggleSection("dss")}
            >
              <div className="space-y-4">
                <SwitchField>
                  <Label>Child is in DSS Custody</Label>
                  <Switch
                    checked={isInDSSCustody}
                    onChange={() => setIsInDSSCustody(!isInDSSCustody)}
                  />
                </SwitchField>

                {isInDSSCustody && (
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <Field>
                      <Label>Social Worker Name</Label>
                      <Input
                        type="text"
                        value={socialWorkerName}
                        placeholder="Social worker's name"
                        onChange={(e) => setSocialWorkerName(e.target.value)}
                      />
                    </Field>
                    <Field>
                      <Label>Social Worker Phone</Label>
                      <Input
                        type="tel"
                        value={socialWorkerPhone}
                        placeholder="(555) 555-5555"
                        onChange={(e) => setSocialWorkerPhone(e.target.value)}
                      />
                    </Field>
                  </div>
                )}
              </div>
            </CollapsibleSection>

            {/* Victimization Information */}
            <CollapsibleSection
              title="Victimization Information"
              description="Abuse exposure and victimization type"
              isExpanded={expandedSections.victimization}
              onToggle={() => toggleSection("victimization")}
            >
              <div className="space-y-4">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <Field>
                    <Label>Type of Secondary Victimization</Label>
                    <Select
                      value={victimizationType}
                      onChange={(e) => setVictimizationType(e.target.value as VictimizationType)}
                    >
                      <option value="">Select...</option>
                      {Object.entries(VICTIMIZATION_TYPE_LABELS).map(([value, label]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ))}
                    </Select>
                  </Field>
                  {victimizationType === "other" && (
                    <Field>
                      <Label>Other Victimization Type</Label>
                      <Input
                        type="text"
                        value={victimizationOther}
                        placeholder="Specify type"
                        onChange={(e) => setVictimizationOther(e.target.value)}
                      />
                    </Field>
                  )}
                </div>

                <SwitchField>
                  <Label>Child has witnessed abuse in the home</Label>
                  <Switch
                    checked={hasWitnessedAbuse}
                    onChange={() => setHasWitnessedAbuse(!hasWitnessedAbuse)}
                  />
                </SwitchField>
              </div>
            </CollapsibleSection>
          </div>
        </DialogBody>
        <DialogActions>
          <Button plain onClick={clearForm}>
            Cancel
          </Button>
          <Button disabled={isSaving} onClick={handleSubmit}>
            {isSaving ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default EditChildButton;
